// @ts-check
/**
 * Time menu: query block child menu wrapper for the date range picker widget
 */
import * as React from 'react';

import {ChildMenuInterface} from 'common/query-builder/query-block/child-menu';
import {SelectorType} from 'common/query-builder/query-block/constants';
import {TrackingUsageContext} from 'common/report/tracking-setup';
import {getCurrentMoment} from 'common/util/date';
import {emitTrackEvent} from 'common/util/dom';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {DATE_RANGE_TYPES} from 'common/widgets/date-range-picker/models/date-range-factory';
import {DisplayContext} from 'common/widgets/date-range-picker/models/types';
import {DateRangePickerReact} from 'common/widgets/date-range-picker-new';
import {getDateRangeLabel} from 'common/widgets/date-range-picker-new/util';

interface TimeMenuProps {
  displayContext?: DisplayContext;
  eventNames?: string[];
  isTimePickerEnabled?: boolean;
  maxDataHistoryDays?: number;
  open?: boolean;
  projectAsOfDate?: string;
  readonly?: boolean;
  selected?: DateRange;
  selectorType?: string;
  trackName?: string;
  setTimePickerEnabled?: (isTimePickerEnabled: boolean) => void;
  onSelect: (ev: any) => void;
  onCancel: () => void;
  onRender?: () => void;
}

export class TimeMenu extends React.PureComponent<TimeMenuProps> implements ChildMenuInterface {
  private divRef = React.createRef<HTMLDivElement>();

  static defaultProps = {
    selected: DateRange,
    displayContext: DisplayContext.FullLength,
    selectorType: SelectorType.Text,
  };

  componentDidUpdate() {
    this.props.onRender?.();
  }

  get displayContext() {
    return this.props.displayContext || DisplayContext.FullLength;
  }

  onDateApply({dateRange}): void {
    this.props.setTimePickerEnabled(dateRange.isTimePickerEnabled);
    this.props.onSelect({detail: dateRange});
    this.trackSelection({
      eventName: `Select date range`,
      props: {
        'date range from': dateRange.from || null,
        'date range to': dateRange.to || null,
        'date range type': dateRange.type,
        'window unit': dateRange.window ? dateRange.window.unit : null,
        'window value': dateRange.window ? dateRange.window.value : null,
      },
    });
  }

  onDateCancel(): void {
    this.props.onCancel();
  }

  trackSelection({props, ...eventData}, cancelable = true) {
    const additionalProps = {
      [`usage context`]: TrackingUsageContext.Report,
      'Event Names': this.props.eventNames,
    };
    if (this.props.trackName) {
      additionalProps[`tab`] = this.props.trackName;
    }
    emitTrackEvent(this.divRef.current, {
      ...eventData,
      props: {
        ...props,
        ...additionalProps,
      },
      cancelable,
    });
  }

  canOpenMenu() {
    return !this.props.readonly;
  }

  shouldCloseOnSelect() {
    return true;
  }

  getPresetMatch() {
    const selected = this.props.selected;
    if (!selected) {
      return null;
    }

    const dateRangeClass = DATE_RANGE_TYPES[this.displayContext];
    return dateRangeClass.getPresetOptions().find((option) => {
      return selected.equals(new DateRange(option.value));
    });
  }

  selectedLabel(): string {
    return getDateRangeLabel(
      this.props.selected,
      this.getPresetMatch(),
      null /* project as of date; we set this to "null" because we're trialing a change where we show the project-as-of-date in the tooltip, not the label */,
      getCurrentMoment({localized: true}),
      this.props.selectorType === `text` ? `none` : `simple`,
    );
  }

  render() {
    const dateRangeAttributes = this.props.selected?.getAttributes();
    return (
      <div ref={this.divRef} className='mp-time-menu'>
        {this.props.open && (
          <DateRangePickerReact
            elref={`dateRangePicker`}
            displayContext={this.displayContext}
            dateRange={dateRangeAttributes}
            isTimePickerEnabled={this.props.isTimePickerEnabled}
            projectAsOfDate={this.props.projectAsOfDate}
            maxHistoryDays={this.props.maxDataHistoryDays}
            trackedUseCase={TrackingUsageContext.QueryBuilder}
            forceHidePresetMenu
            onApply={(ev) => this.onDateApply(ev.detail)}
            onCancel={() => this.onDateCancel()}
          />
        )}
      </div>
    );
  }
}

export const TimeMenuReact = TimeMenu;
